<template>
  <div class="projects">
    <div class="container">
      <!-- Header Section -->
      <section class="projects-hero">
        <h1>My Projects</h1>
        <p class="hero-description">
          A collection of AI-driven fashion tech projects I've worked on with AlphaBake, showcasing innovative solutions for the fashion e-commerce industry.
        </p>
      </section>

      <!-- Filter Section -->
      <section class="filter-section">
        <div class="filter-buttons">
          <button 
            v-for="category in categories" 
            :key="category"
            @click="setActiveFilter(category)"
            :class="['filter-btn', { active: activeFilter === category }]"
          >
            {{ category }}
          </button>
        </div>
      </section>

      <!-- Projects Grid -->
      <section class="projects-grid">
        <div class="projects-container">
          <div 
            v-for="project in filteredProjects" 
            :key="project.id"
            class="project-card"
            @click="selectProject(project)"
          >
            <div class="project-image">
              <div v-if="project.images && project.images.length > 0" class="image-carousel">
                <img 
                  :src="project.images[project.currentImageIndex || 0]" 
                  :alt="project.title"
                  :class="['project-img', { 'small-img': project.id === 2 }]"
                  @click.stop="toggleImage(project)"
                />
                <div v-if="project.images.length > 1" class="image-indicator">
                  {{ (project.currentImageIndex || 0) + 1 }} / {{ project.images.length }}
                </div>
              </div>
              <div v-else class="placeholder-icon">{{ project.icon }}</div>
              <div class="project-overlay">
                <div class="overlay-content">
                  <button class="view-btn">View Details</button>
                  <div class="project-links">
                    <a v-if="project.liveUrl" :href="project.liveUrl" target="_blank" @click.stop>Live Demo</a>
                    <a v-if="project.githubUrl" :href="project.githubUrl" target="_blank" @click.stop>GitHub</a>
                  </div>
                </div>
              </div>
            </div>
            <div class="project-content">
              <h3>{{ project.title }}</h3>
              <p>{{ project.description }}</p>
              <div class="project-tech">
                <span v-for="tech in project.technologies" :key="tech" class="tech-tag">
                  {{ tech }}
                </span>
              </div>
              <div class="project-meta">
                <span class="project-category">{{ project.category }}</span>
                <span class="project-year">{{ project.year }}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Project Modal -->
      <div v-if="selectedProject" class="modal-overlay" @click="closeModal">
        <div class="modal-content" @click.stop>
          <button class="modal-close" @click="closeModal">&times;</button>
          <div class="modal-header">
            <h2>{{ selectedProject.title }}</h2>
            <div class="modal-links">
              <a v-if="selectedProject.liveUrl" :href="selectedProject.liveUrl" target="_blank" class="btn btn-primary">
                Live Demo
              </a>
              <a v-if="selectedProject.githubUrl" :href="selectedProject.githubUrl" target="_blank" class="btn btn-secondary">
                GitHub
              </a>
            </div>
          </div>
          <div class="modal-body">
            <!-- Before & After Images for Virtual Try-On -->
            <div v-if="selectedProject.inputImage && selectedProject.outputImages" class="before-after-container">
              <div class="input-section">
                <h4>Input (Original)</h4>
                <div class="image-container">
                  <img 
                    :src="selectedProject.inputImage" 
                    :alt="selectedProject.title + ' - Input'"
                    class="modal-img input-img"
                  />
                </div>
              </div>
              
              <div class="arrow-section">
                <div class="arrow">→</div>
                <div class="process-label">Virtual Try-On</div>
              </div>
              
              <div class="output-section">
                <h4>Output </h4>
                <div class="image-container">
                  <img 
                    :src="selectedProject.outputImages[selectedProject.currentOutputIndex || 0]" 
                    :alt="selectedProject.title + ' - Output'"
                    class="modal-img output-img"
                    @click="toggleOutputImage(selectedProject)"
                  />
                  <div v-if="selectedProject.outputImages.length > 1" class="output-indicator">
                    <button @click="toggleOutputImage(selectedProject)" class="image-toggle-btn">
                      {{ (selectedProject.currentOutputIndex || 0) + 1 }} / {{ selectedProject.outputImages.length }} - Click to toggle outputs
                    </button>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- Fallback for projects without input/output structure -->
            <div v-else class="modal-image">
              <!-- Videos Section -->
              <div v-if="selectedProject.videos && selectedProject.videos.length > 0" class="modal-video-section">
                <h4>Project Demo</h4>
                <div class="video-container">
                  <video 
                    :src="selectedProject.videos[0]" 
                    controls 
                    class="modal-video"
                    preload="metadata"
                  >
                    Your browser does not support the video tag.
                  </video>
                </div>
              </div>
              
              <!-- Images Section -->
              <div v-if="selectedProject.images && selectedProject.images.length > 0 && selectedProject.id !== 2" class="modal-image-carousel">
                <img 
                  :src="selectedProject.images[selectedProject.currentImageIndex || 0]" 
                  :alt="selectedProject.title"
                  :class="['modal-img', { 'small-img': selectedProject.id === 2 }]"
                  @click="toggleImage(selectedProject)"
                />
                <div v-if="selectedProject.images.length > 1" class="modal-image-indicator">
                  <button @click="toggleImage(selectedProject)" class="image-toggle-btn">
                    {{ (selectedProject.currentImageIndex || 0) + 1 }} / {{ selectedProject.images.length }} - Click to toggle
                  </button>
                </div>
              </div>
              
              <!-- Fallback icon if no media -->
              <div v-if="!selectedProject.images?.length && !selectedProject.videos?.length" class="modal-icon">{{ selectedProject.icon }}</div>
            </div>
            <div class="modal-details">
              <p class="modal-description" v-html="getFormattedDescription(selectedProject)"></p>
              <div class="modal-features">
                <h4>Key Features:</h4>
                <ul>
                  <li v-for="feature in selectedProject.features" :key="feature">{{ feature }}</li>
                </ul>
              </div>
              <div class="modal-tech">
                <h4>Technologies Used:</h4>
                <div class="tech-list">
                  <span v-for="tech in selectedProject.technologies" :key="tech" class="tech-tag">
                    {{ tech }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'

interface Project {
  id: number
  title: string
  description: string
  fullDescription: string
  category: string
  technologies: string[]
  year: string
  icon: string
  images?: string[]
  currentImageIndex?: number
  inputImage?: string
  outputImages?: string[]
  currentOutputIndex?: number
  liveUrl?: string
  githubUrl?: string
  features: string[]
  videos?: string[]
}

const activeFilter = ref('All')
const selectedProject = ref<Project | null>(null)

const categories = ['All', 'AI/ML', 'Fashion Tech', 'Virtual Try-On', 'Cloud Tools', 'Automation', '3D Visualization']

const projects: Project[] = [
  {
    id: 1,
    title: 'AI Virtual Try-On System',
    description: 'Advanced AI-powered virtual try-on technology enabling customers to see how garments look on them digitally',
    fullDescription: 'As a core team member at AlphaBake, I participated in developing and bringing to production a <strong>Virtual Try-On system</strong> for fashion e-commerce. I was involved in the end-to-end process from <strong>design to deployment</strong> of the compute infrastructure, where we implemented a CPU server that interfaced with GPU servers running the AI workflow, enabling fast and reliable try-on experiences.<br><br>We built a <strong>Shopify app</strong> that allowed merchants to seamlessly enable try-on features for their products, making it instantly available on their storefronts. To extend adoption beyond Shopify, I contributed to the development of <strong>robust APIs</strong> for non-Shopify users, along with a <strong>dashboard</strong> that provided merchants visibility into try-ons, conversion metrics (e.g., try-ons vs. add-to-carts), and user activity.<br><br>I also participated in integrating <strong>Razorpay payments</strong> for API users, enabling a smooth monetization flow. This end-to-end solution enhanced customer experience and <strong>significantly increased add-to-cart rates</strong> for merchants.',
    category: 'Virtual Try-On',
    technologies: ['Python', 'Django', 'ComfyUI', 'Fine-tuned LoRA Models', 'JavaScript', 'HTML', 'Vertex AI', 'Cloud Run'],
    year: '2022-Present',
    icon: '👤',
    images: [
      '/images/541263074_1295808128584811_2867038981911573425_n.jpg',
      '/images/1a8c546c-0887-4b97-b019-d66daeed12af_final-tryon.jpg',
      '/images/aea05360-e766-4c7b-815f-ec584d939a36_final-tryon.jpg'
    ],
    currentImageIndex: 0,
    inputImage: '/images/541263074_1295808128584811_2867038981911573425_n.jpg',
    outputImages: [
      '/images/1a8c546c-0887-4b97-b019-d66daeed12af_final-tryon.jpg',
      '/images/aea05360-e766-4c7b-815f-ec584d939a36_final-tryon.jpg'
    ],
    currentOutputIndex: 0,
    features: [
      'Real-time virtual garment fitting',
      'Multiple garment type support',
      'Shopify app for merchant integration',
      'RESTful API for e-commerce platforms',
      'Mobile and web compatibility',
      'Razorpay payments integration',
      'Dashboard for users',
      'Cloud-native deployment on Google Cloud'
    ]
  },
  {
    id: 2,
    title: 'Fab3D - Fabric Visualization Platform',
    description: 'Revolutionary fabric visualization tool converting flat fabric images into realistic stitched garment visuals for fashion e-commerce',
    fullDescription: '<strong>Fab3D is a comprehensive fabric visualization platform</strong> that transforms flat fabric images into realistic stitched garment visuals, enabling customers to preview dresses, suits, and home decor items in draped formats. I participated in developing this platform <strong>end-to-end</strong>, contributing across the <strong>frontend, backend, and supporting microservices</strong>.<br><br>The platform also included a <strong>suit configurator</strong> with nearly 1,000 customizable elements, <strong>Razorpay integration</strong> for secure payments, and <strong>Algolia-powered search</strong> for fast and intelligent product discovery. These features empowered retailers to deliver a <strong>richer, more dynamic catalogue experience</strong> to their customers.<br><br>Sviewer was evolved into <strong>Fab3D</strong> as a more <strong>scalable web-based solution</strong>. <strong>5M+ images downloaded</strong> in Sviewer & Fab3D.',
    category: 'Fashion Tech',
    technologies: ['Vue.js', 'Python', 'Blender', '3D Graphics', 'Computer Vision', 'WebGL','Node.js','Algolia','Razorpay','Firebase'],
    year: '2019-Present',
    icon: '👗',
    images: [
      '/images/fab3d_logo.png',
      
    ],
    videos: [
      '/images/Video-716.mp4'
    ],
    features: [
      'Converts flat fabric images to realistic 3D garment previews',
      'Supports multiple garment types: dresses, suits, home decor',
      'Real-time fabric draping simulation',
      'Web-based platform for broader accessibility',
      'Integration with major e-commerce platforms',
      'Advanced texture mapping and rendering',
      'Customer preview interface for better shopping experience'
    ]
  },
  {
    id: 3,
    title: 'AI/ML Infra',
    description: 'Advanced generative AI workflows with custom LoRA models for personalized fashion catalog content generation',
    fullDescription: 'I developed <strong>advanced generative AI pipelines</strong> using <strong>ComfyUI</strong>, building <strong>custom nodes</strong> to extend functionality and streamline workflows. These pipelines enabled <strong>style-controlled image generation</strong> for virtual try-on applications and <strong>automated fashion catalog content creation</strong>. I also worked on <strong>model swap</strong> and <strong>background swap workflows</strong> to enhance content generation capabilities. Separately, I trained <strong>custom LoRA models</strong> to enable <strong>personalized image generation</strong>, enhancing the style and adaptability of the AI outputs.',
    category: 'AI/ML',
    technologies: ['ComfyUI', 'LoRA', 'Python', 'Generative AI'],
    year: '2022-Present',
    icon: '🎨',
    images: [
      '/images/comfyui.webp',
    ],
    
    features: [
      'Custom LoRA model training',
      'ComfyUI workflow development and optimization',
      'Virtual try-on technology integration',
      'Automated catalog content generation',
      'Model swap and background swap workflows',
    ]
  },
  {
    id: 4,
    title: 'Model Swap & Background Swap System',
    description: 'AI-powered model and background replacement technology for fashion catalog automation',
    fullDescription: 'Built an advanced AI system for automatically swapping models and backgrounds in fashion photography. This technology enables fashion brands to quickly adapt their catalog images for different markets, demographics, and seasonal campaigns without expensive photo shoots, using sophisticated generative AI and image processing techniques.',
    category: 'AI/ML',
    technologies: ['ComfyUI', 'Generative AI', 'Image Segmentation', 'Python', 'Computer Vision', 'Neural Networks'],
    year: '2022-Present',
    icon: '🔄',
    images: [
      '/images/modelBgSwap/450140480_4512436555648339_5193320949152961466_n.jpg',
      '/images/modelBgSwap/450309891_443339391990645_4925138342764744339_n.jpg'
    ],
    currentImageIndex: 0,
    inputImage: '/images/modelBgSwap/450140480_4512436555648339_5193320949152961466_n.jpg',
    outputImages: [
      '/images/modelBgSwap/450309891_443339391990645_4925138342764744339_n.jpg'
    ],
    currentOutputIndex: 0,
    features: [
      'Automatic model replacement in fashion photos',
      'Seamless background swapping technology',
      'Preserves garment details and lighting',
      'Batch processing for catalog automation',
      'Multiple demographic options',
      'Season and market-specific adaptations',
      'Quality-preserving AI algorithms',
      'Cost-effective alternative to photo shoots'
    ]
  },
  {
    id: 5,
    title: 'Cloud Cost Optimization Utility',
    description: 'Multi-cloud management tool for Azure, AWS, and GCP to optimize GPU resource costs for AI workloads',
    fullDescription: 'Built an <strong>intelligent cloud management utility</strong> that automatically starts and stops cloud machines across <strong>Azure, AWS, and GCP</strong> based on usage patterns, optimizing <strong>compute costs</strong> and <strong>GPU resource allocation</strong> for <strong>generative AI workflows</strong>.<br><br>Worked on setting up <strong>Cloud Run</strong> and <strong>Vertex AI</strong> to execute and scale <strong>GenAI Vision workflows</strong>, handling <strong>3K–5K try-ons per day</strong>.<br><br>Implemented <strong>offline try-ons</strong> by spinning up <strong>AWS spot instances</strong> to run workflows, automatically shutting them down once image generation was complete, significantly improving <strong>cost efficiency</strong> and <strong>scalability</strong>.',
    category: 'Cloud Tools',
    technologies: ['Python', 'AWS SDK', 'Azure CLI', 'GCP APIs', 'Docker', 'Monitoring', 'Cost Analytics'],
    year: '2021-Present',
    icon: '☁️',
    features: [
      'Multi-cloud support (AWS, Azure, GCP)',
      'Usage pattern analysis and prediction',
      'Automatic start/stop based on demand',
      'GPU resource optimization for AI workloads',
      'Cloud Run and Vertex AI setup',
      'Offline try-ons implementation',
      'Cost efficiency and scalability improvements'
    ]
  },
  {
    id: 6,
    title: 'Sviewer - Desktop Fabric Visualization',
    description: 'Desktop-based fabric visualization tool focused on Indian ethnic wear, particularly sarees',
    fullDescription: 'Sviewer was my initial foray into fabric visualization technology, specifically designed for Indian ethnic wear like sarees. This desktop application laid the foundation for what would later become the web-based Fab3D platform, proving the concept of digital fabric visualization in the fashion industry.',
    category: '3D Visualization',
    technologies: ['Python', 'Django', 'Blender', 'GIMP', 'Photoshop', '3D Modeling', 'Texture Mapping'],
    year: '2018-2019',
    icon: '🥻',
    features: [
      'Specialized saree visualization',
      'Desktop application with local processing',
      'Traditional Indian wear focus',
    ]
  },
  {
    id: 7,
    title: 'Performance Tracking Dashboard',
    description: 'Internal tool for monitoring client product usage and employee KPIs with automated reporting',
    fullDescription: 'Designed and implemented a comprehensive performance tracking system that monitors client product usage patterns and employee KPIs. The dashboard provides automated reports and data visualizations, helping management make informed decisions about product development and resource allocation.',
    category: 'Analytics',
    technologies: ['Vue.js', 'Node.js', 'PostgreSQL',  'Python', 'Data Analytics'],
    year: '2020-2021',
    icon: '📊',
    features: [
      'Real-time client usage monitoring',
      'Employee KPI tracking and analysis',
      'Automated report generation',
      'Interactive data visualizations',
      'Custom dashboard creation',
      'Performance trend analysis',
      'Alert system for anomalies',
      'Export functionality for management reports'
    ]
  },
  {
    id: 8,
    title: 'Photoshop & Blender Automation Add-ons',
    description: 'Custom automation plugins to streamline 3D garment modeling workflows and reduce manual design time',
    fullDescription: 'Created sophisticated automation add-ons for both Photoshop and Blender to streamline 3D garment modeling workflows. These tools significantly reduced manual design time by automating repetitive tasks and standardizing design processes across the team.',
    category: 'Automation',
    technologies: ['Python', 'Blender API', 'Adobe Scripting', 'JavaScript', 'Workflow Automation'],
    year: '2020-Present',
    icon: '🔧',
    features: [
      'Custom Blender plugins for garment modeling',
      'Photoshop automation scripts',
      'Batch processing capabilities',
      'Standardized workflow templates',
      '60% reduction in manual design time',
      'Team productivity optimization'
    ]
  },
  {
    id: 9,
    title: 'Blender Work Tracking Plugin',
    description: 'Analytics plugin to map designer activity and identify repetitive tasks for automation opportunities',
    fullDescription: 'Developed a specialized Blender plugin that tracks and analyzes designer activity patterns to identify repetitive tasks suitable for automation. This tool provides insights into workflow efficiency and helps prioritize automation development efforts.',
    category: 'Automation',
    technologies: ['Python', 'Blender API', 'Data Analytics', 'SQLite', 'Activity Tracking'],
    year: '2021-2022',
    icon: '📈',
    features: [
      'Real-time activity tracking in Blender',
      'Pattern recognition for repetitive tasks',
      'Designer productivity analytics',
      'Time allocation analysis',
      'Team performance comparisons',
    ]
  }
]

const filteredProjects = computed(() => {
  if (activeFilter.value === 'All') {
    return projects
  }
  return projects.filter(project => project.category === activeFilter.value)
})

function setActiveFilter(category: string) {
  activeFilter.value = category
}

function selectProject(project: Project) {
  selectedProject.value = project
  // Add modal-open class for body scroll prevention
  document.body.classList.add('modal-open')
  document.body.style.overflow = 'hidden'
}

function closeModal() {
  selectedProject.value = null
  // Remove modal-open class and restore scroll
  document.body.classList.remove('modal-open')
  document.body.style.overflow = 'auto'
}

function openProject6() {
  // Close current modal first
  closeModal()
  // Find project 6 and open it
  const project6 = projects.find(p => p.id === 6)
  if (project6) {
    // Use setTimeout to ensure modal close animation completes
    setTimeout(() => {
      selectProject(project6)
    }, 100)
  }
}

function getFormattedDescription(project: Project) {
  if (project.id === 2) {
    // For Fab3D project, make Sviewer clickable
    return project.fullDescription.replace(
      'Sviewer',
      '<span class="clickable-text" onclick="window.openProject6()" >Sviewer</span>'
    )
  }
  return project.fullDescription
}

function toggleImage(project: Project) {
  if (project.images && project.images.length > 1) {
    project.currentImageIndex = ((project.currentImageIndex || 0) + 1) % project.images.length
  }
}

function toggleOutputImage(project: Project) {
  if (project.outputImages && project.outputImages.length > 1) {
    project.currentOutputIndex = ((project.currentOutputIndex || 0) + 1) % project.outputImages.length
  }
}

onMounted(() => {
  // Close modal on escape key
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      closeModal()
    }
  })
  
  // Make openProject6 available globally for onclick handlers
  ;(window as any).openProject6 = openProject6
})
</script>

<style scoped>
.projects {
  padding: var(--space-20) 0;
}

.projects-hero {
  text-align: center;
  margin-bottom: var(--space-16);
}

.projects-hero h1 {
  margin-bottom: var(--space-6);
  color: var(--text-color);
}

.hero-description {
  font-size: var(--font-size-lg);
  color: var(--text-muted);
  max-width: 700px;
  margin: 0 auto;
  line-height: 1.7;
}

.filter-section {
  margin-bottom: var(--space-12);
}

.filter-buttons {
  display: flex;
  justify-content: center;
  gap: var(--space-4);
  flex-wrap: wrap;
}

.filter-btn {
  padding: var(--space-3) var(--space-6);
  border: 1px solid var(--border-color);
  background: var(--background-color);
  color: var(--text-muted);
  border-radius: var(--radius-lg);
  cursor: pointer;
  transition: all var(--transition-fast);
  font-weight: 500;
  font-size: var(--font-size-sm);
}

.filter-btn:hover,
.filter-btn.active {
  background: var(--primary-color);
  color: white;
  border-color: var(--primary-color);
}

.projects-container {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(400px, 100%), 1fr));
  gap: var(--space-8);
}

.project-card {
  background: var(--background-color);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-xl);
  overflow: hidden;
  transition: all var(--transition-normal);
  cursor: pointer;
}

.project-card:hover {
  transform: translateY(-4px);
  box-shadow: var(--shadow-lg);
}

.project-image {
  height: 250px;
  background: var(--surface-color);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
}

.placeholder-icon {
  font-size: 5rem;
  opacity: 0.3;
  transition: all var(--transition-normal);
}

.image-carousel {
  width: 100%;
  height: 100%;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.project-img {
  width: 100%;
  height: 100%;
  /* margin-top:22em; */
  object-fit: contain;
  cursor: pointer;
  transition: all var(--transition-normal);
}

.project-img.small-img {
  width: 40%;
  height: 40%;
  object-fit: contain;
}

.project-img:hover {
  transform: scale(1.05);
}

.image-indicator {
  position: absolute;
  bottom: var(--space-2);
  right: var(--space-2);
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: var(--space-1) var(--space-2);
  border-radius: var(--radius-md);
  font-size: var(--font-size-xs);
  font-weight: 500;
}

.project-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(37, 99, 235, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: all var(--transition-normal);
}

.project-card:hover .project-overlay {
  opacity: 1;
}

.project-card:hover .placeholder-icon {
  transform: scale(0.8);
  opacity: 0.1;
}

.overlay-content {
  text-align: center;
  color: white;
}

.view-btn {
  padding: var(--space-3) var(--space-6);
  background: white;
  color: var(--primary-color);
  border: none;
  border-radius: var(--radius-md);
  font-weight: 600;
  cursor: pointer;
  margin-bottom: var(--space-4);
  transition: all var(--transition-fast);
}

.view-btn:hover {
  transform: scale(1.05);
}

.project-links {
  display: flex;
  gap: var(--space-3);
  justify-content: center;
}

.project-links a {
  color: white;
  text-decoration: underline;
  font-size: var(--font-size-sm);
}

.project-content {
  padding: var(--space-6);
}

.project-content h3 {
  margin-bottom: var(--space-3);
  color: var(--text-color);
  font-size: var(--font-size-lg);
}

.project-content p {
  margin-bottom: var(--space-4);
  color: var(--text-muted);
  line-height: 1.6;
}

.project-tech {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
  margin-bottom: var(--space-4);
}

.tech-tag {
  padding: var(--space-1) var(--space-3);
  background: var(--surface-color);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-md);
  font-size: var(--font-size-sm);
  color: var(--text-muted);
  font-weight: 500;
}

.project-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: var(--font-size-sm);
  color: var(--text-muted);
}

.project-category {
  background: var(--primary-color);
  color: white;
  padding: var(--space-1) var(--space-3);
  border-radius: var(--radius-md);
  font-weight: 500;
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: var(--space-4);
}

.modal-content {
  background: var(--background-color);
  border-radius: var(--radius-xl);
  width: 100%;
  max-width: 900px;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
}

.modal-close {
  position: absolute;
  top: var(--space-4);
  right: var(--space-4);
  background: none;
  border: none;
  font-size: 2rem;
  cursor: pointer;
  color: var(--text-muted);
  z-index: 1001;
}

.modal-header {
  padding: var(--space-8) var(--space-8) var(--space-4);
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: var(--space-4);
}

.modal-header h2 {
  margin: 0;
  color: var(--text-color);
  flex: 1;
  font-size: var(--font-size-2xl);
}

.modal-links {
  display: flex;
  gap: var(--space-3);
}

.modal-body {
  padding: 0 var(--space-8) var(--space-8);
}

.modal-image {
  text-align: center;
  padding: var(--space-8);
  background: var(--surface-color);
  border-radius: var(--radius-lg);
  margin-bottom: var(--space-6);
}

.modal-icon {
  font-size: 6rem;
  opacity: 0.7;
}

.modal-image-carousel {
  width: 100%;
  height: 100%;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-img {
  width: 100%;
  max-height: 400px;
  object-fit: contain;
  cursor: pointer;
  border-radius: var(--radius-lg);
  transition: all var(--transition-normal);
}

.modal-img:hover {
  transform: scale(1.02);
}

.modal-image-indicator {
  position: absolute;
  bottom: var(--space-4);
  left: 50%;
  transform: translateX(-50%);
}

.image-toggle-btn {
  background: rgba(0, 0, 0, 0.8);
  color: white;
  border: none;
  padding: var(--space-2) var(--space-4);
  border-radius: var(--radius-lg);
  font-size: var(--font-size-sm);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.image-toggle-btn:hover {
  background: rgba(0, 0, 0, 0.9);
  transform: scale(1.05);
}

/* Before & After Layout */
.before-after-container {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: var(--space-6);
  align-items: center;
  margin-bottom: var(--space-8);
  padding: var(--space-6);
  background: var(--surface-color);
  border-radius: var(--radius-lg);
  border: 2px solid var(--border-color);
}

.input-section,
.output-section {
  text-align: center;
}

.input-section h4,
.output-section h4 {
  margin-bottom: var(--space-4);
  color: var(--text-color);
  font-size: var(--font-size-lg);
  font-weight: 600;
}

.input-section h4 {
  color: var(--primary-color);
}

.output-section h4 {
  color: var(--accent-color);
}

.image-container {
  position: relative;
  background: white;
  border-radius: var(--radius-lg);
  overflow: hidden;
  box-shadow: var(--shadow-md);
}

.input-img,
.output-img {
  width: 100%;
  max-height: 350px;
  object-fit: contain;
  display: block;
}

.output-img {
  cursor: pointer;
  transition: all var(--transition-normal);
}

.output-img:hover {
  transform: scale(1.02);
}

.arrow-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-2);
}

.arrow {
  font-size: 2rem;
  color: var(--primary-color);
  font-weight: bold;
  animation: pulse 2s infinite;
}

.process-label {
  font-size: var(--font-size-sm);
  color: var(--text-muted);
  font-weight: 500;
  writing-mode: vertical-rl;
  text-orientation: mixed;
  white-space: nowrap;
}

@keyframes pulse {
  0%, 100% { 
    opacity: 1;
    transform: scale(1);
  }
  50% { 
    opacity: 0.7;
    transform: scale(1.1);
  }
}

.output-indicator {
  position: absolute;
  bottom: var(--space-2);
  left: 50%;
  transform: translateX(-50%);
}

.modal-description {
  font-size: var(--font-size-lg);
  line-height: 1.7;
  color: var(--text-muted);
  margin-bottom: var(--space-6);
}

.modal-description .clickable-text {
  color: #2563eb !important; /* Bright blue color */
  cursor: pointer !important;
  text-decoration: underline !important;
  font-weight: 700 !important;
  transition: all var(--transition-fast);
  /* border-bottom: 2px solid #2563eb !important; */
  padding-bottom: 1px;
  display: inline-block;
  /* background-color: rgba(37, 99, 235, 0.1) !important; */
  padding: 2px 4px !important;
  /* border-radius: 4px !important; */
}

.modal-description .clickable-text:hover {
  color: #1d4ed8 !important; /* Darker blue on hover */
  text-decoration: none !important;
  border-bottom-color: #1d4ed8 !important;
  transform: scale(1.05);
  background-color: rgba(29, 78, 216, 0.2) !important;
  padding: 3px 6px !important;
}

.modal-features,
.modal-tech {
  margin-bottom: var(--space-6);
}

.modal-features h4,
.modal-tech h4 {
  margin-bottom: var(--space-4);
  color: var(--text-color);
}

.modal-features ul {
  list-style: none;
  padding: 0;
}

.modal-features li {
  padding: var(--space-2) 0;
  color: var(--text-muted);
  position: relative;
  padding-left: var(--space-6);
  line-height: 1.6;
}

.modal-features li::before {
  content: '✓';
  position: absolute;
  left: 0;
  color: var(--primary-color);
  font-weight: bold;
}

.tech-list {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-3);
}

/* Video Styles */
.modal-video-section {
  margin-bottom: var(--space-6);
}

.modal-video-section h4 {
  margin-bottom: var(--space-4);
  color: var(--text-color);
  font-size: var(--font-size-lg);
  font-weight: 600;
  text-align: center;
}

.video-container {
  position: relative;
  background: white;
  border-radius: var(--radius-lg);
  overflow: hidden;
  box-shadow: var(--shadow-md);
}

.modal-video {
  width: 100%;
  max-height: 400px;
  object-fit: contain;
  display: block;
  border-radius: var(--radius-lg);
}

/* Mobile responsiveness */
@media (max-width: 768px) {
  .projects {
    padding: var(--space-8) 0;
  }
  
  /* Hero section mobile optimization */
  .projects-hero {
    margin-bottom: var(--space-12);
    text-align: center;
  }
  
  .projects-hero h1 {
    font-size: 2rem;
    margin-bottom: var(--space-4);
  }
  
  .hero-description {
    font-size: var(--font-size-base);
    line-height: 1.6;
    padding: 0 var(--space-4);
  }
  
  /* Filter section mobile optimization */
  .filter-section {
    margin-bottom: var(--space-8);
    padding: 0 var(--space-4);
  }
  
  .filter-buttons {
    justify-content: flex-start;
    overflow-x: auto;
    padding-bottom: var(--space-3);
    gap: var(--space-3);
    -webkit-overflow-scrolling: touch;
  }
  
  .filter-btn {
    padding: var(--space-3) var(--space-4);
    font-size: var(--font-size-base);
    min-width: max-content;
    white-space: nowrap;
    flex-shrink: 0;
  }
  
  /* Projects grid mobile optimization */
  .projects-container {
    grid-template-columns: 1fr;
    gap: var(--space-6);
    padding: 0 var(--space-4);
  }
  
  .project-card {
    border-radius: var(--radius-lg);
  }
  
  .project-image {
    height: 200px;
  }
  
  .project-img {
    object-fit: contain;
  }
  
  .project-img.small-img {
    width: 50%;
    height: 50%;
  }
  
  .project-content {
    padding: var(--space-4);
  }
  
  .project-content h3 {
    font-size: var(--font-size-lg);
    margin-bottom: var(--space-2);
  }
  
  .project-content p {
    font-size: var(--font-size-sm);
    line-height: 1.5;
    margin-bottom: var(--space-3);
  }
  
  .project-tech {
    gap: var(--space-1);
    margin-bottom: var(--space-3);
  }
  
  .tech-tag {
    font-size: 0.75rem;
    padding: var(--space-1) var(--space-2);
  }
  
  .project-meta {
    font-size: 0.75rem;
  }
  
  .project-category {
    padding: var(--space-1) var(--space-2);
    font-size: 0.75rem;
  }
  
  /* Modal mobile optimization */
  .modal-overlay {
    padding: 0;
    align-items: stretch;
    justify-content: stretch;
    overflow: hidden;
    width: 100vw;
    width: 100dvw;
    max-width: 100vw;
    box-sizing: border-box;
  }
  
  .modal-content {
    max-width: 100%;
    width: 100%;
    height: 100vh;
    height: 100dvh; /* Dynamic viewport height for better mobile support */
    max-height: none;
    margin: 0;
    border-radius: 0;
    position: relative;
    overflow-y: auto;
    overflow-x: hidden;
    display: flex;
    flex-direction: column;
    -webkit-overflow-scrolling: touch;
    box-sizing: border-box;
  }
  
  /* Ensure all modal content respects width constraints */
  .modal-content * {
    max-width: 100%;
    box-sizing: border-box;
  }
  
  /* Fix for any potential text overflow */
  .modal-content p,
  .modal-content div,
  .modal-content span,
  .modal-content h1,
  .modal-content h2,
  .modal-content h3,
  .modal-content h4,
  .modal-content h5,
  .modal-content h6 {
    word-wrap: break-word;
    overflow-wrap: break-word;
    hyphens: auto;
  }
  
  .modal-close {
    position: absolute;
    top: calc(var(--space-4) + env(safe-area-inset-top, 0px));
    right: calc(var(--space-4) + env(safe-area-inset-right, 0px));
    font-size: 1.5rem;
    background: var(--surface-color);
    color: var(--text-color);
    border: 1px solid var(--border-color);
    border-radius: 50%;
    width: 44px;
    height: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1002;
    transition: all var(--transition-fast);
    touch-action: manipulation;
    flex-shrink: 0;
  }
  
  .modal-close:hover {
    background: var(--primary-color);
    color: white;
    border-color: var(--primary-color);
  }
  
  .modal-header {
    flex-direction: column;
    align-items: stretch;
    padding: calc(var(--space-10) + 44px + env(safe-area-inset-top, 0px)) var(--space-4) var(--space-4);
    gap: var(--space-4);
    background: var(--background-color);
    flex-shrink: 0;
    border-bottom: 1px solid var(--border-color);
    width: 100%;
    box-sizing: border-box;
    position: relative;
  }
  
  .modal-header h2 {
    font-size: var(--font-size-xl);
    line-height: 1.2;
    text-align: left;
    padding-right: 0;
    margin: 0;
    width: 100%;
    box-sizing: border-box;
    word-wrap: break-word;
    overflow-wrap: break-word;
  }
  
  .modal-links {
    justify-content: flex-start;
    gap: var(--space-3);
    flex-wrap: wrap;
    width: 100%;
    box-sizing: border-box;
  }
  
  .modal-links .btn {
    font-size: var(--font-size-sm);
    padding: var(--space-3) var(--space-4);
    min-height: 44px;
    touch-action: manipulation;
    flex: 1;
    text-align: center;
    min-width: 120px;
    max-width: calc(50% - var(--space-2));
    border-radius: var(--radius-lg);
    box-sizing: border-box;
  }
  
  .modal-body {
    padding: var(--space-4);
    flex: 1;
    overflow-x: hidden;
    width: 100%;
    box-sizing: border-box;
  }
  
  /* Modal images and videos mobile */
  .modal-image {
    padding: var(--space-3);
    margin-bottom: var(--space-4);
    border-radius: var(--radius-md);
    width: 100%;
    box-sizing: border-box;
  }
  
  .modal-img {
    max-height: 280px;
    border-radius: var(--radius-md);
    width: 100%;
    height: auto;
    object-fit: contain;
    display: block;
    box-sizing: border-box;
  }
  
  .modal-video {
    max-height: 220px;
    border-radius: var(--radius-md);
    width: 100%;
    height: auto;
    display: block;
    box-sizing: border-box;
  }
  
  .modal-video-section {
    width: 100%;
    box-sizing: border-box;
  }
  
  .modal-video-section h4 {
    text-align: left;
    margin-bottom: var(--space-3);
    font-size: var(--font-size-lg);
    width: 100%;
    word-wrap: break-word;
  }
  
  .modal-image-carousel {
    margin-bottom: var(--space-4);
    width: 100%;
    box-sizing: border-box;
  }
  
  .modal-image-indicator {
    position: static;
    transform: none;
    margin-top: var(--space-3);
    text-align: center;
    width: 100%;
    box-sizing: border-box;
  }
  
  /* Before-after layout mobile */
  .before-after-container {
    grid-template-columns: 1fr;
    grid-template-rows: auto auto auto;
    gap: var(--space-5);
    text-align: center;
    padding: var(--space-4);
    margin-bottom: var(--space-6);
    border-radius: var(--radius-lg);
    width: 100%;
    box-sizing: border-box;
    overflow: hidden;
  }
  
  .input-section {
    order: 1;
    width: 100%;
    box-sizing: border-box;
  }
  
  .arrow-section {
    order: 2;
    padding: var(--space-3) 0;
    width: 100%;
    box-sizing: border-box;
  }
  
  .output-section {
    order: 3;
    width: 100%;
    box-sizing: border-box;
  }
  
  .arrow {
    transform: rotate(90deg);
    font-size: 1.75rem;
    color: var(--primary-color);
  }
  
  .process-label {
    writing-mode: horizontal-tb;
    text-orientation: initial;
    font-size: var(--font-size-sm);
    color: var(--primary-color);
    font-weight: 600;
    margin-top: var(--space-2);
    word-wrap: break-word;
  }
  
  .input-section h4,
  .output-section h4 {
    font-size: var(--font-size-lg);
    margin-bottom: var(--space-3);
    font-weight: 600;
    width: 100%;
    word-wrap: break-word;
  }
  
  .input-img,
  .output-img {
    max-height: 240px;
    border-radius: var(--radius-md);
    width: 100%;
    height: auto;
    object-fit: contain;
    display: block;
    box-sizing: border-box;
  }
  
  .image-container {
    margin-bottom: var(--space-3);
    border-radius: var(--radius-md);
    overflow: hidden;
    width: 100%;
    box-sizing: border-box;
  }
  
  .output-indicator {
    position: static;
    transform: none;
    margin-top: var(--space-3);
    width: 100%;
    box-sizing: border-box;
  }
  
  .output-indicator .image-toggle-btn {
    background: var(--primary-color);
    color: white;
    padding: var(--space-3) var(--space-4);
    border-radius: var(--radius-md);
    font-size: var(--font-size-sm);
    min-height: 44px;
    width: 100%;
    touch-action: manipulation;
    box-sizing: border-box;
    border: none;
    word-wrap: break-word;
  }
  
  /* Modal details mobile */
  .modal-description {
    font-size: var(--font-size-base);
    line-height: 1.65;
    margin-bottom: var(--space-5);
    color: var(--text-color);
    width: 100%;
    box-sizing: border-box;
    word-wrap: break-word;
    overflow-wrap: break-word;
  }
  
  .modal-features,
  .modal-tech {
    margin-bottom: var(--space-5);
    width: 100%;
    box-sizing: border-box;
  }
  
  .modal-features h4,
  .modal-tech h4 {
    font-size: var(--font-size-lg);
    margin-bottom: var(--space-4);
    font-weight: 600;
    color: var(--text-color);
    width: 100%;
    word-wrap: break-word;
  }
  
  .modal-features ul {
    width: 100%;
    box-sizing: border-box;
    padding: 0;
    margin: 0;
  }
  
  .modal-features li {
    padding: var(--space-2) 0;
    padding-left: var(--space-5);
    font-size: var(--font-size-base);
    line-height: 1.6;
    color: var(--text-muted);
    width: 100%;
    box-sizing: border-box;
    word-wrap: break-word;
    overflow-wrap: break-word;
    position: relative;
  }
  
  .modal-features li::before {
    content: '✓';
    position: absolute;
    left: 0;
    color: var(--primary-color);
    font-weight: bold;
    font-size: var(--font-size-lg);
  }
  
  .tech-list {
    gap: var(--space-2);
    justify-content: flex-start;
    width: 100%;
    box-sizing: border-box;
    flex-wrap: wrap;
  }
  
  .tech-list .tech-tag {
    font-size: var(--font-size-sm);
    padding: var(--space-2) var(--space-3);
    background: var(--primary-color);
    color: white;
    border: none;
    font-weight: 500;
    word-break: break-word;
    max-width: 100%;
    box-sizing: border-box;
  }
  
  /* Touch improvements */
  .image-toggle-btn {
    padding: var(--space-3) var(--space-4);
    font-size: var(--font-size-sm);
    touch-action: manipulation;
    border: none;
    cursor: pointer;
    transition: all var(--transition-fast);
    min-height: 44px;
  }
  
  .image-toggle-btn:hover {
    background: rgba(0, 0, 0, 0.9);
    transform: scale(1.02);
  }
  
  .view-btn {
    padding: var(--space-3) var(--space-6);
    font-size: var(--font-size-base);
    touch-action: manipulation;
    min-height: 44px;
  }
  
  /* Additional mobile modal improvements */
  .modal-overlay {
    backdrop-filter: blur(4px);
  }
  
  .modal-content {
    animation: slideInModal 0.25s ease-out;
    /* Safe area support for devices with notches */
    padding-top: env(safe-area-inset-top);
    padding-bottom: env(safe-area-inset-bottom);
    padding-left: env(safe-area-inset-left);
    padding-right: env(safe-area-inset-right);
  }
  

  
  @keyframes slideInModal {
    from {
      opacity: 0;
      transform: scale(0.95);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }
  
  /* Prevent body scroll when modal is open */
  /* Note: Add this to index.html: <meta name="viewport" content="width=device-width, initial-scale=1, viewport-fit=cover"> */
  body:has(.modal-overlay) {
    overflow: hidden;
    position: fixed;
    width: 100%;
    height: 100%;
  }
  
  /* Fallback for browsers without :has() support */
  @supports not (selector(:has(*))) {
    body.modal-open {
      overflow: hidden;
      position: fixed;
      width: 100%;
      height: 100%;
    }
  }
}

/* Extra small mobile devices */
@media (max-width: 480px) {
  .projects {
    padding: var(--space-6) 0;
  }
  
  .container {
    padding: 0 var(--space-3);
  }
  
  .projects-hero h1 {
    font-size: 1.75rem;
  }
  
  .hero-description {
    font-size: var(--font-size-sm);
    padding: 0 var(--space-2);
  }
  
  .filter-section {
    padding: 0 var(--space-2);
  }
  
  .filter-btn {
    padding: var(--space-2) var(--space-3);
    font-size: var(--font-size-sm);
  }
  
  .projects-container {
    padding: 0 var(--space-2);
    gap: var(--space-4);
  }
  
  .project-image {
    height: 180px;
  }
  
  .project-content {
    padding: var(--space-3);
  }
  
  .project-content h3 {
    font-size: var(--font-size-base);
  }
  
  .project-content p {
    font-size: 0.75rem;
  }
  
  .tech-tag {
    font-size: 0.6rem;
    padding: 2px 6px;
  }
  
  .modal-content {
    margin: 0;
    height: 100vh;
    height: 100dvh;
    border-radius: 0;
    padding-top: env(safe-area-inset-top);
    padding-bottom: env(safe-area-inset-bottom);
  }
  
  .modal-close {
    top: calc(var(--space-2) + env(safe-area-inset-top, 0px));
    right: calc(var(--space-2) + env(safe-area-inset-right, 0px));
    width: 40px;
    height: 40px;
    font-size: 1.25rem;
  }
  
  .modal-header {
    padding: calc(var(--space-8) + 40px + env(safe-area-inset-top, 0px)) var(--space-3) var(--space-3);
    gap: var(--space-3);
  }
  
  .modal-header h2 {
    font-size: var(--font-size-lg);
    line-height: 1.3;
  }
  
  .modal-links .btn {
    padding: var(--space-2) var(--space-3);
    font-size: 0.875rem;
    min-height: 40px;
    min-width: 100px;
  }
  
  .modal-body {
    padding: var(--space-3);
    padding-bottom: calc(var(--space-4) + env(safe-area-inset-bottom, 0px));
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
    -webkit-overflow-scrolling: touch;
    width: 100%;
    box-sizing: border-box;
  }
  
  .modal-image {
    padding: var(--space-2);
    margin-bottom: var(--space-3);
  }
  
  .modal-img {
    max-height: 200px;
  }
  
  .modal-video {
    max-height: 180px;
  }
  
  .before-after-container {
    padding: var(--space-3);
    gap: var(--space-4);
    margin-bottom: var(--space-4);
  }
  
  .input-section h4,
  .output-section h4 {
    font-size: var(--font-size-base);
    margin-bottom: var(--space-2);
  }
  
  .input-img,
  .output-img {
    max-height: 180px;
  }
  
  .arrow {
    font-size: 1.5rem;
  }
  
  .process-label {
    font-size: 0.75rem;
  }
  
  .modal-description {
    font-size: var(--font-size-sm);
    line-height: 1.6;
    margin-bottom: var(--space-4);
  }
  
  .modal-features,
  .modal-tech {
    margin-bottom: var(--space-4);
  }
  
  .modal-features h4,
  .modal-tech h4 {
    font-size: var(--font-size-base);
    margin-bottom: var(--space-3);
  }
  
  .modal-features li {
    font-size: var(--font-size-sm);
    padding: var(--space-1) 0;
    padding-left: var(--space-4);
    line-height: 1.5;
  }
  
  .modal-features li::before {
    font-size: var(--font-size-base);
  }
  
  .tech-list .tech-tag {
    font-size: 0.75rem;
    padding: var(--space-1) var(--space-2);
  }
  
  .image-toggle-btn {
    padding: var(--space-2) var(--space-3);
    font-size: 0.875rem;
    min-height: 40px;
  }
  
  .output-indicator .image-toggle-btn {
    padding: var(--space-2) var(--space-3);
    min-height: 40px;
  }
}
</style>

<style>
/* Global styles for clickable text - not scoped */
.clickable-text {
  color: #2563eb !important;
  cursor: pointer !important;
  text-decoration: underline !important;
  font-weight: 700 !important;
  /* background-color: rgba(37, 99, 235, 0.1) !important; */
  padding: 2px 4px !important;
  /* border-radius: 4px !important; */
  /* border-bottom: 2px solid #2563eb !important; */
  display: inline-block !important;
  transition: all 0.2s ease !important;
}

.clickable-text:hover {
  color: #1d4ed8 !important;
  text-decoration: none !important;
  background-color: rgba(29, 78, 216, 0.2) !important;
  padding: 3px 6px !important;
  transform: scale(1.05) !important;
  border-bottom-color: #1d4ed8 !important;
}
</style> 